import { ArrowLeftOutlined } from '@ant-design/icons'
import { BasicModal } from '@life-line-lab/components-react';
import { Row } from 'antd';
import { DetailsContainer } from 'Components/shared/details-container';
import ModalContent from 'Components/shared/modal-content';
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ModalTranslationContent, Statuses, DataListType } from 'Types/common.types';
import { DriverApprovalStatus, DriverStatus } from 'Types/new_driver_types';
import "./driver-profile-two.scss"
import TabContentFooter from './profile-tab-footer/tab-content-footer';

interface DriverProfileTwoProps {
    canEdit?:boolean;
    canDelete?:boolean;
    onBack: () => void;
    driverDetails: Partial<any>;
    onActivateDriver?:()=>void;
    onDriverApproval?:(profileID:string,status:Statuses,reason:string)=>void;
}

const DriverProfileTwo:React.FC<DriverProfileTwoProps> = ({
    canEdit,
    canDelete,
    driverDetails,
    onBack,
    onActivateDriver,
    onDriverApproval,
}) => {

    const { t } = useTranslation();

    // add info for different profile sections
    let driverInfo:DataListType[] = [
        {
            label: t("label.fullName"),
            value: driverDetails?.fullName,
        },
        {
            label: t("label.nationalID").toUpperCase(),
            value: driverDetails?.passportID,
        },
        {
            label: t("label.emailAddress").toUpperCase(),
            value: driverDetails?.emailAddress,
        },
        {
            label: t("label.mobileNo"),
            value: driverDetails?.mobileNumber,
        },
        {
            label: t("label.gender"),
            value: driverDetails?.gender,
        },
        {
            label: t("label.homeAddress"),
            value: driverDetails?.fullAddress,
        },
    ];

    const [driverInfoState, setDriverInfoState] = useState(driverInfo);

    const vehicleInfo:DataListType[] = [
        {
            label: t("label.riderType"),
            value: driverDetails?.riderType,
        },
        {
            label: t("label.driverOwnsVehicle"),
            value: driverDetails?.vehicleType ? 'Yes' : 'No',
        },
        {
            label: t("label.vehicleType"),
            value: driverDetails?.vehicleType,
        },
        {
            label: t("label.vehicleModel"),
            value: driverDetails?.vehicleModel,
        },
        {
            label: t("label.plateNo"),
            value: driverDetails?.vehiclePlateNumber,
        },
    ];

    const paymentInfo:DataListType[] = [
        // driverDetails?.payrollDetails?.xxx (in case BE changes the response format
        // to include all paymentInfo into a payrollDetails object
        // remove this comment and the data retrieval condition if the accessed property
        // has been confirmed
        {
            label: t("label.bankType"),
            value: driverDetails?.bankType ?? driverDetails?.payrollDetails?.bankType,
        },
        {
            label: t("label.accountNo"),
            value: driverDetails?.accountNo ?? driverDetails?.payrollDetails?.accountNo,
        },
        {
            label: t("label.socsoNo"),
            value: driverDetails?.socsoNo ?? driverDetails?.payrollDetails?.socsoNo,
        },
        {
            label: t("label.epfNo"),
            value: driverDetails?.epfNo ?? driverDetails?.payrollDetails?.epfNo,
        },
        {
            label: t("label.incomeTax"),
            value: driverDetails?.incomeTax ?? driverDetails?.payrollDetails?.incomeTax,
        },
        {
            label: t("label.hrdfNo"),
            value: driverDetails?.hrdfNo ?? driverDetails?.payrollDetails?.hrdfNo,
        },
        {
            label: t("label.zakatNo"),
            value: driverDetails?.zakatNo ?? driverDetails?.payrollDetails?.zakatNo,
        },
    ];

    const emergencyContactsInfo:DataListType[] = [
        {
            label: t("label.name"),
            value: driverDetails?.emergencyContactName,
        },
        {
            label: t("label.relationship"),
            value: driverDetails?.relationship,
        },
        {
            label: t("label.contactNo"),
            value: driverDetails?.emergencyContactNo?.dialCode ? `+${driverDetails?.emergencyContactNo?.dialCode} ${driverDetails?.emergencyContactNo?.number}` : '-',
        },
    ];

    const attachmentsInfo:DataListType[] = [
        {
            label: t("label.nricPassport").toUpperCase(),
            value: driverDetails?.nric_passport?.fileName,
            link: driverDetails?.nric_passport?.file_url
        },
        {
            label: t("label.drivingLicense"),
            value: driverDetails?.driving_license?.fileName,
            link: driverDetails?.driving_license?.file_url
        },
        {
            label: t("label.roadTax"),
            value: driverDetails?.road_tax?.fileName,
            link: driverDetails?.road_tax?.file_url
        },
        {
            label: t("label.photoOfVehicle"),
            value: driverDetails?.photo_of_vehicle?.fileName,
            link: driverDetails?.photo_of_vehicle?.file_url
        },
    ];

    const initialModal = {
        open: false, record: {}, type: "approve"
    };

    const [modal, setModal] = useState<any>(initialModal);

    // add column for rejected or deactivate reason
    useEffect(() => {
        let driverInfoDetails = [...driverInfo];
        if(driverDetails?.approvalStatus === DriverApprovalStatus.REJECTED){
          // setDriverInfoState([...driverInfo,])
          driverInfoDetails.push({
            label: t("label.reasonForRejection"),
            value: driverDetails?.rejectedReason,
            color:'red',
          })
        }
        if(driverDetails?.tempDeleteInfo?.tempDelete){
          // setDriverInfoState([...driverInfo,])
          driverInfoDetails.push({
            label: t("label.reasonForDeactivate"),
            value: driverDetails?.tempDeleteInfo?.tempDeletedReason,
            color:'red',
          })
        }
        setDriverInfoState(driverInfoDetails)
    }, [driverDetails])

    const handleSetModal = (type) => {
        setModal({
            ...modal, open: true, type: type,
            record: driverDetails,
        })
    }

    const handleResetModalState = () => {
        setModal(initialModal);
    }

    // ? write as a helper function
    const handleModalTranslation = () : ModalTranslationContent => {
        if (modal.type === DriverApprovalStatus.APPROVED) {
            return {
                title: t("label.confirmDriverApproval").toUpperCase(),
                sureTxt: t("txt.areYouSureApproveAccount"),
                contentToConfirm: t("txt.driverID").toUpperCase(),
                actionLabel: t("action.approve")
            }
        } else if (modal.type === DriverApprovalStatus.REJECTED) {
            return {
                title: t("label.rejectDriver").toUpperCase(),
                sureTxt: t("txt.areYouSureRejectAccount"),
                contentToConfirm: t("txt.driverID").toUpperCase(),
                actionLabel: t("action.reject")
            }
        } else if (modal.type === DriverStatus.DEACTIVATE) {
            return {
                title: t("label.deactivateDriver").toUpperCase(),
                sureTxt: t("txt.areYouSureDeactivateAccount"),
                contentToConfirm: t("txt.driverID").toUpperCase(),
                actionLabel: t("action.deactivate")
            }
        } else if (modal.type === DriverStatus.ACTIVATE) {
            return {
                title: t("label.activateDriver").toUpperCase(),
                sureTxt: t("txt.areYouSureActivateAccount"),
                contentToConfirm: t("txt.driverID").toUpperCase(),
                actionLabel: t("action.activate")
            }
        } else return {
            title: '-',
            sureTxt: '-',
            contentToConfirm: '-',
            actionLabel: '-'
        }
    }

    return (
        <div className="p-20 my-20 deactivated-driver-details-container">
            <div className="my-10 main-header status-selector">
                <span className="cursor-pointer back-action"
                    onClick={() => { onBack() }}
                >
                    <ArrowLeftOutlined />
                    <span className="ml-5">{t("txt.back")}</span>
                </span>
                <span className="center header-title">
                    {t("title.driverDetails").toUpperCase()}  |  {driverDetails?.code}
                </span>
              <span>
                <span className={`status ${driverDetails?.approvalStatus}`}>
                  {driverDetails?.approvalStatus}
                </span>
              </span>
            </div>

            {/* TODO: remove duplication of code */}
            <DetailsContainer
                listOfData={driverInfoState}
            />

            <div className="center my-10 header-title">
                {t("title.vehicleInformation").toUpperCase()}
            </div>

            <DetailsContainer
                listOfData={vehicleInfo}
            />

            <div className="center my-10 header-title">
                {t("title.paymentDetails").toUpperCase()}
            </div>

            <DetailsContainer
                listOfData={paymentInfo}
            />

            <div className="center my-10 header-title">
                {t("title.emergencyContacts").toUpperCase()}
            </div>

            <DetailsContainer
                listOfData={emergencyContactsInfo}
            />

            <div className="center my-10 header-title">
                {t("title.attachments").toUpperCase()}
            </div>

            <DetailsContainer
                listOfData={attachmentsInfo}
            />

            {/* Footer */}
            <Row justify='end'>
                <TabContentFooter
                    canEdit={canEdit}
                    canDelete={canDelete}
                    handleActivate={onActivateDriver}
                    handleSetModal={handleSetModal}
                    driverDetails={driverDetails}
                />
            </Row>

            {/* Confirm modal */}
            <BasicModal
                isOpen={modal.open}
                isClosable={true}
                isMaskClosable={false}
                onClose={() => {handleResetModalState()}}
            >
                <ModalContent
                    modal={modal}
                    onHandleModalTranslation={handleModalTranslation}
                    onResetModal={handleResetModalState}
                    onApproveOrReject={onDriverApproval}
                    onActivate={onActivateDriver}
                />
            </BasicModal>
        </div>
    )
}

export default DriverProfileTwo
